import socketService from './socket.service';

interface Message {
  _id: string;
  type: 'text' | 'image' | 'voice' | 'video' | 'file' | 'location' | 'emoji' | 'system';
  content: string;
  from?: string | { id: string; nickname: string; avatar: string };
  groupId?: string;
  readAt?: Date;
  replyTo?: string;
  isRead?: boolean;
  createdAt: Date;
  file?: {
    id: string;
    url: string;
    originalName: string;
    size: number;
    mimeType: string;
  };
  location?: { lat: number; lng: number; address?: string };
  recalledAt?: Date;
  isPinned?: boolean;
}

export class MessageService {
  private _messages: Map<string, Message[]> = new Map();
  private _lastReplyTo: string | null = null;
  private _lastMessageId: string | null = null;

  async sendMessage(data: {
    type: Message['type'];
    content?: string;
    to?: string;
    groupId?: string;
    replyTo?: string;
    fileId?: string;
    location?: Message['location'];
    ephemeral?: boolean;
    readReceipt?: boolean;
  }): Promise<Message> {
    return new Promise((resolve, reject) => {
      socketService.emit('sendMessage', data);

      const handleMessageSent = (message: Message) => {
        this.addMessage(message);
        resolve(message);
      };

      socketService.on('message:sent', handleMessageSent);
      socketService.on('error', reject);
    });
  }

  addMessage(message: Message, chatId?: string): void {
    const id =
      chatId ||
      message.groupId ||
      (typeof message.from === 'object' ? message.from?.id : message.from) ||
      'unknown';
    if (!this._messages.has(id)) {
      this._messages.set(id, []);
    }
    this._messages.get(id)!.push(message);
  }

  getAll(chatId?: string): Message[] {
    if (chatId) {
      return this._messages.get(chatId) || [];
    }
    // 返回所有消息
    const allMessages: Message[] = [];
    this._messages.forEach((messages) => {
      allMessages.push(...messages);
    });
    return allMessages;
  }

  getById(messageId: string): Message | null {
    for (const messages of this._messages.values()) {
      const message = messages.find((m) => m._id === messageId);
      if (message) return message;
    }
    return null;
  }

  markAsRead(messageId: string, userId: string): void {
    const message = this.getById(messageId);
    if (message) {
      message.readAt = new Date();
      message.isRead = true;
      // 发送已读回执到服务器
      socketService.emit('readReceipt', { messageId, userId });
    }
  }

  async recall(messageId: string): Promise<void> {
    socketService.emit('recallMessage', { messageId });

    const handleRecalled = ({ messageId }: { messageId: string }) => {
      const message = this.getById(messageId);
      if (message) {
        message.recalledAt = new Date();
        message.content = '消息已撤回';
      }
      socketService.off('message:recalled', handleRecalled);
    };

    socketService.on('message:recalled', handleRecalled);
  }

  async forward(
    messageId: string,
    toUserIds: string[],
    toGroupIds: string[],
  ): Promise<{ count: number }> {
    return new Promise((resolve, reject) => {
      socketService.emit('forwardMessage', {
        messageId,
        toUserIds,
        toGroupIds,
      });

      const handleForwarded = ({ count }: { count: number }) => {
        resolve({ count });
      };

      socketService.on('message:forwarded', handleForwarded);
      socketService.on('error', reject);
    });
  }

  async search(
    keyword: string,
    options?: {
      type?: string;
      startDate?: Date;
      endDate?: Date;
      limit?: number;
      offset?: number;
    },
  ): Promise<{ messages: Message[]; total: number }> {
    return new Promise((resolve, reject) => {
      socketService.emit('searchMessages', {
        keyword,
        ...options,
      });

      const handleSearchResult = (result: { messages: Message[]; total: number }) => {
        resolve(result);
      };

      socketService.on('message:searchResult', handleSearchResult);
      socketService.on('error', reject);
    });
  }

  async togglePin(messageId: string): Promise<void> {
    socketService.emit('pinMessage', { messageId });

    const handlePinned = ({ messageId, isPinned }: { messageId: string; isPinned: boolean }) => {
      const message = this.getById(messageId);
      if (message) {
        message.isPinned = isPinned;
      }
      socketService.off('message:pinned', handlePinned);
    };

    socketService.on('message:pinned', handlePinned);
  }

  async getPinned(): Promise<Message[]> {
    return new Promise((resolve, reject) => {
      socketService.emit('getPinnedMessages');

      const handlePinnedList = (messages: Message[]) => {
        resolve(messages);
      };

      socketService.on('message:pinnedList', handlePinnedList);
      socketService.on('error', reject);
    });
  }

  joinGroup(groupId: string): void {
    socketService.emit('joinGroup', { groupId });
  }

  leaveGroup(groupId: string): void {
    socketService.emit('leaveGroup', { groupId });
  }

  sendTyping(targetId: string, isTyping: boolean): void {
    socketService.emit('typing', { targetId, isTyping });
  }

  loadHistory(chatId: string, before?: Date, limit: number = 50): Promise<Message[]> {
    return new Promise((resolve, reject) => {
      socketService.emit('loadHistory', {
        chatId,
        before,
        limit,
      });

      const handleHistoryLoaded = (messages: Message[]) => {
        // 按时间正序排列（旧消息在前，新消息在后）
        resolve(messages.reverse());
      };

      socketService.on('historyLoaded', handleHistoryLoaded);
      socketService.on('error', reject);
    });
  }

  clear(chatId?: string): void {
    if (chatId) {
      this._messages.delete(chatId);
    } else {
      this._messages.clear();
    }
  }

  get lastReplyTo(): string | null {
    return this._lastReplyTo;
  }

  set lastReplyTo(value: string | null) {
    this._lastReplyTo = value;
  }

  get lastMessageId(): string | null {
    return this._lastMessageId;
  }

  set lastMessageId(value: string | null) {
    this._lastMessageId = value;
  }
}

export default new MessageService();
